<template>
    <div class="groups-container">
        <!-- Stats Cards -->
        <div class="stats-grid">
            <div class="stat-card">
                <div class="stat-icon" style="background: #e0e7ff; color: #4f46e5;">
                    <i class="fas fa-layer-group"></i>
                </div>
                <div class="stat-content">
                    <div class="stat-value">{{ groups.length }}</div>
                    <div class="stat-label">Total Groupes</div>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon" style="background: #dcfce7; color: #16a34a;">
                    <i class="fas fa-desktop"></i>
                </div>
                <div class="stat-content">
                    <div class="stat-value">{{ totalDevicesInGroups }}</div>
                    <div class="stat-label">Devices assignés</div>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon" style="background: #dbeafe; color: #2563eb;">
                    <i class="fas fa-users"></i>
                </div>
                <div class="stat-content">
                    <div class="stat-value">{{ totalUsersInGroups }}</div>
                    <div class="stat-label">Utilisateurs avec groupes</div>
                </div>
            </div>
        </div>

        <!-- Filters and Actions Bar -->
        <div class="filters-bar">
            <div class="filters-left">
                <div class="search-box">
                    <i class="fas fa-search"></i>
                    <input
                        type="text"
                        v-model="searchQuery"
                        placeholder="Rechercher un groupe..."
                    >
                </div>
            </div>
            <div class="filters-right">
                <button class="btn-create" @click="showCreateForm = true">
                    <i class="fas fa-plus"></i>
                    Créer un groupe
                </button>
            </div>
        </div>

        <!-- Table Card -->
        <div class="table-card">
            <div class="table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Devices</th>
                            <th>Date de création</th>
                            <th class="actions-header">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="isLoading">
                            <td colspan="4" class="loading-cell">
                                <div class="loading-spinner">
                                    <div class="spinner"></div>
                                    <p>Chargement...</p>
                                </div>
                            </td>
                        </tr>
                        <tr v-else-if="filteredGroups.length === 0">
                            <td colspan="4" class="empty-cell">
                                <div class="empty-state">
                                    <i class="fas fa-inbox"></i>
                                    <p>Aucun groupe trouvé</p>
                                </div>
                            </td>
                        </tr>
                        <tr
                            v-for="group in paginatedGroups"
                            :key="group.id"
                            class="table-row"
                        >
                            <td>
                                <div class="group-name">{{ group.name }}</div>
                            </td>
                            <td>
                                <span class="device-count">
                                    <i class="fas fa-desktop"></i>
                                    {{ getDeviceCount(group.id) }}
                                </span>
                            </td>
                            <td>
                                <span class="create-date">{{ formatDate(group.created_at) }}</span>
                            </td>
                            <td class="actions-cell">
                                <div class="action-buttons">
                                    <button
                                        class="action-btn"
                                        @click="editGroup(group)"
                                        title="Modifier"
                                    >
                                        <i class="fas fa-pen"></i>
                                    </button>
                                    <button
                                        class="action-btn action-btn-danger"
                                        @click="confirmDelete(group)"
                                        title="Supprimer"
                                    >
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div class="pagination" v-if="filteredGroups.length > pageSize">
                <div class="pagination-info">
                    Affichage de {{ startIndex + 1 }} à {{ Math.min(endIndex, filteredGroups.length) }}
                    sur {{ filteredGroups.length }} groupes
                </div>
                <div class="pagination-controls">
                    <button
                        class="pagination-btn"
                        :disabled="currentPage === 1"
                        @click="currentPage--"
                    >
                        <i class="fas fa-chevron-left"></i>
                    </button>
                    <span class="pagination-pages">
                        Page {{ currentPage }} / {{ totalPages }}
                    </span>
                    <button
                        class="pagination-btn"
                        :disabled="currentPage === totalPages"
                        @click="currentPage++"
                    >
                        <i class="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Create/Edit Modal -->
        <div v-if="showCreateForm || showEditForm" class="modal-overlay" @click="closeForm">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3 class="modal-title">
                        <i :class="showEditForm ? 'fas fa-edit' : 'fas fa-plus'"></i>
                        {{ showEditForm ? 'Modifier le groupe' : 'Créer un groupe' }}
                    </h3>
                    <button class="modal-close" @click="closeForm">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="modal-body">
                    <form @submit.prevent="submitForm">
                        <div class="form-group">
                            <label for="groupName" class="form-label">
                                Nom du groupe <span class="required">*</span>
                            </label>
                            <input
                                type="text"
                                class="form-input"
                                id="groupName"
                                v-model="formData.name"
                                :class="{ error: errors.name }"
                                placeholder="Ex: Groupe Paris"
                            >
                            <span class="error-message" v-if="errors.name">{{ errors.name }}</span>
                        </div>
                    </form>
                </div>
                <div class="modal-footer">
                    <button class="btn-secondary" @click="closeForm">
                        Annuler
                    </button>
                    <button
                        class="btn-primary"
                        @click="submitForm"
                        :disabled="isSubmitting"
                    >
                        <i class="fas" :class="isSubmitting ? 'fa-spinner fa-spin' : 'fa-save'"></i>
                        {{ isSubmitting ? 'Enregistrement...' : 'Enregistrer' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
            <div class="modal-content modal-sm" @click.stop>
                <div class="modal-header">
                    <h3 class="modal-title modal-title-danger">
                        <i class="fas fa-exclamation-triangle"></i>
                        Confirmer la suppression
                    </h3>
                    <button class="modal-close" @click="showDeleteModal = false">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="modal-body" v-if="currentGroup">
                    <p>Êtes-vous sûr de vouloir supprimer ce groupe ?</p>
                    <div class="alert alert-warning">
                        <strong>{{ currentGroup.name }}</strong>
                    </div>
                    <p class="text-muted">Les devices associés à ce groupe ne seront pas supprimés.</p>
                </div>
                <div class="modal-footer">
                    <button class="btn-secondary" @click="showDeleteModal = false">
                        Annuler
                    </button>
                    <button class="btn-danger" @click="deleteGroup">
                        <i class="fas fa-trash"></i>
                        Supprimer
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import fetch from '@/utils/fetch';
import { useToast } from 'vue-toastification';

export default {
    name: 'GroupsTable',
    data() {
        return {
            groups: [],
            allDevices: [],
            allUsers: [],
            isLoading: false,
            isSubmitting: false,
            searchQuery: '',
            currentPage: 1,
            pageSize: 30,
            currentGroup: null,
            showCreateForm: false,
            showEditForm: false,
            showDeleteModal: false,
            formData: {
                id: null,
                name: ''
            },
            errors: {}
        };
    },
    computed: {
        filteredGroups() {
            if (!this.searchQuery) {
                return this.groups;
            }
            const query = this.searchQuery.toLowerCase();
            return this.groups.filter(group =>
                (group.name || '').toLowerCase().includes(query)
            );
        },
        totalDevicesInGroups() {
            return this.allDevices.filter(d => d.group_id).length;
        },
        totalUsersInGroups() {
            return this.allUsers.filter(u => u.groups && u.groups.length > 0).length;
        },
        totalPages() {
            return Math.ceil(this.filteredGroups.length / this.pageSize);
        },
        startIndex() {
            return (this.currentPage - 1) * this.pageSize;
        },
        endIndex() {
            return this.startIndex + this.pageSize;
        },
        paginatedGroups() {
            return this.filteredGroups.slice(this.startIndex, this.endIndex);
        }
    },
    mounted() {
        this.loadData();
    },
    methods: {
        async loadData() {
            this.isLoading = true;
            try {
                const groupsResponse = await fetch({ method: 'GET', url: 'device-groups' });
                this.groups = groupsResponse.data.data || groupsResponse.data;

                const devicesResponse = await fetch({ method: 'GET', url: 'device-lite' });
                this.allDevices = devicesResponse.data.data || devicesResponse.data;

                try {
                    await this.$store.dispatch('users/list/getItems');
                    this.allUsers = this.$store.state.users.list.items || [];
                } catch (error) {
                    console.warn('Impossible de charger les utilisateurs:', error);
                }
            } catch (error) {
                console.error('Erreur lors du chargement:', error);
                const toast = useToast();
                toast.error('Erreur lors du chargement des données');
            } finally {
                this.isLoading = false;
            }
        },

        getDeviceCount(groupId) {
            return this.allDevices.filter(d => d.group_id === groupId).length;
        },

        formatDate(dateString) {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        },

        editGroup(group) {
            this.currentGroup = group;
            this.formData = {
                id: group.id,
                name: group.name
            };
            this.showEditForm = true;
        },

        confirmDelete(group) {
            this.currentGroup = group;
            this.showDeleteModal = true;
        },

        async deleteGroup() {
            const toast = useToast();
            try {
                await fetch({
                    method: 'DELETE',
                    url: `device-groups/${this.currentGroup.id}`
                });
                toast.success('Groupe supprimé avec succès');
                this.showDeleteModal = false;
                this.currentGroup = null;
                await this.loadData();
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
                toast.error('Erreur lors de la suppression du groupe');
            }
        },

        async submitForm() {
            this.errors = {};
            if (!this.formData.name || this.formData.name.trim() === '') {
                this.errors.name = 'Le nom du groupe est requis';
                return;
            }

            this.isSubmitting = true;
            const toast = useToast();

            try {
                const data = {
                    name: this.formData.name.trim()
                };

                if (this.showEditForm) {
                    await fetch({
                        method: 'PUT',
                        url: `device-groups/${this.formData.id}`,
                        data: data
                    });
                    toast.success('Groupe mis à jour avec succès');
                } else {
                    await fetch({
                        method: 'POST',
                        url: 'device-groups',
                        data: data
                    });
                    toast.success('Groupe créé avec succès');
                }

                this.closeForm();
                await this.loadData();

            } catch (error) {
                console.error('Erreur lors de l\'enregistrement:', error);
                const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de l\'enregistrement';
                toast.error(errorMessage);
            } finally {
                this.isSubmitting = false;
            }
        },

        closeForm() {
            this.showCreateForm = false;
            this.showEditForm = false;
            this.currentGroup = null;
            this.formData = {
                id: null,
                name: ''
            };
            this.errors = {};
        }
    },
    watch: {
        searchQuery() {
            this.currentPage = 1;
        }
    }
};
</script>

<style scoped>
.groups-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

/* Stats Grid */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1rem;
}

.stat-card {
    background: white;
    border-radius: 0.75rem;
    padding: 1.25rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-icon {
    width: 56px;
    height: 56px;
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    flex-shrink: 0;
}

.stat-content {
    flex: 1;
}

.stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: #111827;
    line-height: 1;
    margin-bottom: 0.25rem;
}

.stat-label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
}

/* Filters Bar */
.filters-bar {
    background: white;
    border-radius: 0.75rem;
    padding: 1rem 1.25rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filters-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
}

.search-box {
    display: flex;
    align-items: center;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;
    min-width: 250px;
}

.search-box i {
    color: #9ca3af;
    margin-right: 0.5rem;
}

.search-box input {
    border: none;
    background: transparent;
    outline: none;
    flex: 1;
    font-size: 0.875rem;
    color: #111827;
}

.search-box input::placeholder {
    color: #9ca3af;
}

.btn-create {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: #4f46e5;
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.btn-create:hover {
    background: #4338ca;
    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
}

/* Table Card */
.table-card {
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.table-wrapper {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table thead {
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
}

.data-table th {
    padding: 0.75rem 1.5rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
}

.data-table th.actions-header {
    text-align: right;
}

.data-table td {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: middle;
}

.data-table tbody tr:hover {
    background: #f9fafb;
}

.data-table tbody tr:last-child td {
    border-bottom: none;
}

.group-name {
    font-weight: 600;
    color: #111827;
}

.device-count {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    background: #e0e7ff;
    color: #4f46e5;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
}

.device-count i {
    font-size: 0.875rem;
}

.create-date {
    color: #6b7280;
    font-size: 0.875rem;
}

.actions-cell {
    text-align: right;
}

.action-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
}

.action-btn {
    width: 36px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    background: white;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.action-btn:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #111827;
}

.action-btn-danger:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #dc2626;
}

.loading-cell {
    padding: 0 !important;
}

.loading-spinner {
    padding: 4rem 2rem;
    text-align: center;
    color: #9ca3af;
}

.spinner {
    width: 40px;
    height: 40px;
    margin: 0 auto 1rem;
    border: 3px solid #e5e7eb;
    border-top-color: #4f46e5;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.loading-spinner p {
    font-size: 0.875rem;
    margin: 0;
}

.empty-cell {
    padding: 0 !important;
}

.empty-state {
    padding: 4rem 2rem;
    text-align: center;
    color: #9ca3af;
}

.empty-state i {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
}

.empty-state p {
    font-size: 0.875rem;
    margin: 0;
}

/* Pagination */
.pagination {
    padding: 1rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fafafa;
}

.pagination-info {
    font-size: 0.875rem;
    color: #6b7280;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.pagination-btn {
    width: 36px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    background: white;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pagination-btn:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #d1d5db;
}

.pagination-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.pagination-pages {
    font-size: 0.875rem;
    color: #374151;
    font-weight: 500;
    padding: 0 0.75rem;
}

/* Modal */
.modal-overlay {
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    backdrop-filter: blur(2px);
}

.modal-content {
    background: white;
    border-radius: 1rem;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    max-width: 500px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-content.modal-sm {
    max-width: 400px;
}

.modal-header {
    padding: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.modal-title-danger {
    color: #dc2626;
}

.modal-close {
    width: 36px;
    height: 36px;
    border: none;
    background: #f3f4f6;
    color: #6b7280;
    cursor: pointer;
    border-radius: 0.5rem;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-close:hover {
    background: #e5e7eb;
    color: #111827;
}

.modal-body {
    padding: 1.5rem;
}

.modal-footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
}

/* Form */
.form-group {
    margin-bottom: 1rem;
}

.form-group:last-child {
    margin-bottom: 0;
}

.form-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
}

.required {
    color: #dc2626;
}

.form-input {
    width: 100%;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
    color: #111827;
    outline: none;
    transition: all 0.2s;
}

.form-input:focus {
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.form-input.error {
    border-color: #dc2626;
}

.form-input.error:focus {
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.error-message {
    display: block;
    font-size: 0.75rem;
    color: #dc2626;
    margin-top: 0.375rem;
}

/* Buttons */
.btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: #4f46e5;
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
    background: #4338ca;
}

.btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-secondary {
    background: white;
    color: #374151;
    border: 1px solid #e5e7eb;
    padding: 0.625rem 1.25rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-secondary:hover {
    background: #f9fafb;
    border-color: #d1d5db;
}

.btn-danger {
    background: #dc2626;
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-danger:hover {
    background: #b91c1c;
}

/* Alert */
.alert {
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    border-left: 4px solid;
}

.alert-warning {
    background: #fef3c7;
    border-color: #f59e0b;
}

.text-muted {
    color: #6b7280;
    font-size: 0.875rem;
}

/* Responsive */
@media (max-width: 1024px) {
    .filters-bar {
        flex-direction: column;
        align-items: stretch;
    }

    .filters-left {
        flex-direction: column;
        align-items: stretch;
    }

    .search-box {
        min-width: 100%;
    }
}

@media (max-width: 768px) {
    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .data-table th,
    .data-table td {
        padding: 0.75rem 1rem;
        font-size: 0.875rem;
    }

    .action-buttons {
        flex-direction: column;
        gap: 0.25rem;
    }

    .pagination {
        flex-direction: column;
        gap: 1rem;
    }
}

@media (max-width: 640px) {
    .stats-grid {
        grid-template-columns: 1fr;
    }

    .filters-bar {
        padding: 1rem;
    }
}
</style>
